import { Fragment, useState, type ReactNode } from 'react';
import { ChevronRight, FolderOpen, Folder, Inbox } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { formatAmount } from '../../utils/formatters';
import { monedasDelNodo } from '../../utils/agrupadores';
import type { Moneda, NodoArbol } from '../../types';
import { cn } from '@/lib/utils';

interface Props<TItem extends { id: string }> {
  nodos: NodoArbol<TItem>[];
  moneda: Moneda | 'todas';
  /** Con búsqueda activa conviene ver todo abierto. */
  expandirTodo?: boolean;
  /** Acción sobre un agrupador completo: "Pagar" en egresos, "Cobrar" en cobros. */
  verbo: string;
  vacio: string;
  canAccionar: boolean;
  disabled?: boolean;
  onAccionNodo: (nodo: NodoArbol<TItem>, moneda: Moneda) => void;
  renderItem: (item: TItem, profundidad: number) => ReactNode;
}

export const fmtMonto = (v: number, moneda: Moneda) => `${moneda} $ ${formatAmount(v, moneda)}`;

const claveNodo = (nodo: NodoArbol<unknown>) => (nodo.id === null ? 'sin-agrupar' : String(nodo.id));

/**
 * Árbol de agrupadores con sus pendientes, compartido por egresos y cobros.
 * "Sin agrupar" no se acciona en bloque.
 */
export function ArbolPendientes<TItem extends { id: string }>({
  nodos,
  moneda,
  expandirTodo = false,
  verbo,
  vacio,
  canAccionar,
  disabled = false,
  onAccionNodo,
  renderItem,
}: Props<TItem>) {
  const [abiertos, setAbiertos] = useState<Set<string>>(new Set());

  const toggle = (clave: string) =>
    setAbiertos((prev) => {
      const next = new Set(prev);
      if (next.has(clave)) next.delete(clave);
      else next.add(clave);
      return next;
    });

  if (nodos.length === 0) {
    return <p className="py-10 text-center text-sm text-muted-foreground">{vacio}</p>;
  }

  const renderNodo = (nodo: NodoArbol<TItem>, profundidad: number) => {
    const clave = claveNodo(nodo);
    const abierto = expandirTodo || abiertos.has(clave);
    const monedas = monedasDelNodo(nodo, moneda);
    const cantidad = monedas.reduce((acc, m) => acc + nodo.totales[m].cantidad, 0);
    const IconoNodo = nodo.id === null ? Inbox : abierto ? FolderOpen : Folder;

    return (
      <div key={clave} className="border-b border-gray-100 last:border-b-0">
        <div
          className="flex flex-wrap items-center gap-x-3 gap-y-2 py-3 pr-4 hover:bg-slate-50"
          style={{ paddingLeft: `${16 + profundidad * 20}px` }}
        >
          <button
            type="button"
            onClick={() => toggle(clave)}
            aria-expanded={abierto}
            className="flex grow basis-48 items-center gap-2 text-left"
          >
            <ChevronRight className={cn('size-4 shrink-0 text-muted-foreground transition-transform', abierto && 'rotate-90')} />
            <IconoNodo className="size-4 shrink-0 text-slate-500" />
            <span className="truncate text-sm font-semibold text-gray-900">{nodo.nombre}</span>
            <Badge variant="outline" className="shrink-0 text-[11px] font-normal">
              {cantidad} {cantidad === 1 ? 'ítem' : 'ítems'}
            </Badge>
          </button>

          <div className="flex flex-wrap items-center gap-3">
            {monedas.map((m) => (
              <div key={m} className="flex items-center gap-2">
                <div className="text-right leading-tight">
                  <p className="text-sm font-semibold tabular-nums text-slate-700" data-testid={`saldo-${clave}-${m}`}>
                    {fmtMonto(nodo.totales[m].saldo, m)}
                  </p>
                  {nodo.totales[m].pagado > 0 && (
                    <p className="text-[11px] tabular-nums text-muted-foreground">
                      de {fmtMonto(nodo.totales[m].total, m)}
                    </p>
                  )}
                </div>
                {canAccionar && nodo.id !== null && (
                  <Button
                    size="sm"
                    disabled={disabled}
                    className="h-8 rounded-lg px-3 text-xs"
                    onClick={() => onAccionNodo(nodo, m)}
                    aria-label={`${verbo} ${nodo.nombre} en ${m}`}
                  >
                    {monedas.length > 1 ? `${verbo} ${m}` : verbo}
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>

        {abierto && (
          <>
            {nodo.hijos.map((h) => renderNodo(h, profundidad + 1))}
            {nodo.items.map((item) => (
              <Fragment key={item.id}>{renderItem(item, profundidad + 1)}</Fragment>
            ))}
          </>
        )}
      </div>
    );
  };

  return <div>{nodos.map((n) => renderNodo(n, 0))}</div>;
}
